/**
 * ExamScoreImageUpload — like ScoreImageUpload but for exams (per-section
 * scores instead of a single total). On successful extraction it calls
 * `onCandidatesExtracted` so the parent ExamScoreGrid can pre-populate
 * its editable cells; teacher reviews + clicks the existing "保存全部"
 * to commit. We never persist directly here — same review-before-save
 * pattern as the assignment flow.
 */
import { useState } from "react";

import {
  uploadExamScoreImage,
  type ExamSection,
  type ExamVisionCandidate,
  type Student,
} from "../api/school";
import { CustomPromptInput } from "./CustomPromptInput";

interface ExamScoreImageUploadProps {
  examId: string;
  examTitle: string;
  sections: ExamSection[];
  students: Student[];
  onCandidatesExtracted: (candidates: ExamVisionCandidate[]) => void;
}

export function ExamScoreImageUpload({
  examId,
  examTitle,
  sections,
  students,
  onCandidatesExtracted,
}: ExamScoreImageUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [phase, setPhase] = useState<"pick" | "uploading" | "done">("pick");
  const [mockMode, setMockMode] = useState(false);
  const [count, setCount] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  async function handleUpload() {
    if (!file) return;
    setErr(null);
    setPhase("uploading");
    try {
      const resp = await uploadExamScoreImage(
        examId,
        file,
        customPrompt.trim() || undefined,
      );
      setMockMode(resp.mock_mode);
      setCount(resp.candidates.length);
      onCandidatesExtracted(resp.candidates);
      setPhase("done");
    } catch (e) {
      setErr((e as Error).message);
      setPhase("pick");
    }
  }

  function reset() {
    setPhase("pick");
    setFile(null);
    setCount(0);
    setMockMode(false);
  }

  // Used only for the placeholder hint so the teacher knows which sections
  // exist in this exam — helpful when writing additional instructions.
  const sectionPreview = sections
    .filter((s) => s.max_score > 0)
    .map((s) => `${s.name}(${s.max_score})`)
    .join(" · ");

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 mb-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-700">
          📷 拍照识别考试成绩（AI 提取）
        </h4>
        <span className="text-xs text-slate-500 truncate ml-3">{examTitle}</span>
      </div>

      {phase === "pick" && (
        <>
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block text-sm mb-2"
          />
          {file && (
            <div className="text-xs text-slate-500 mb-2">
              已选择：<span className="font-mono">{file.name}</span> ·{" "}
              {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
          {sectionPreview && (
            <div className="text-xs text-slate-500 mb-2">
              本次考试分项：{sectionPreview}
            </div>
          )}
          <div className="mb-2">
            <CustomPromptInput
              value={customPrompt}
              onChange={setCustomPrompt}
              label="额外指示（可选）"
              placeholder="例如：分数模糊请标记 uncertain；若某部分未参加请省略该 key"
              surface="exam"
              context={{
                exam_title: examTitle,
                section_count: sections.filter((s) => s.max_score > 0).length,
                roster_size: students.length,
              }}
            />
          </div>
          {err && <div className="text-xs text-red-600 mb-2">{err}</div>}
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || students.length === 0}
            className="px-3 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
          >
            上传识别
          </button>
          {students.length === 0 && (
            <div className="text-xs text-amber-700 mt-1">
              ⚠️ 班级花名册为空——请先在班级页面添加学生再使用图片识别。
            </div>
          )}
        </>
      )}

      {phase === "uploading" && (
        <div className="text-sm text-slate-600 italic">
          AI 正在识别图片中的分项成绩… (~5 秒)
        </div>
      )}

      {phase === "done" && (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-emerald-700">
            ✅ 已识别 {count} 名学生的分项成绩，请在下方表格中核对，确认无误后点击 “保存全部”。
            {mockMode && (
              <span className="ml-2 text-xs text-amber-700">
                （演示模式：模拟数据）
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={reset}
            className="px-3 py-1 rounded border border-slate-200 bg-white text-xs hover:bg-slate-50 whitespace-nowrap"
          >
            再上传一张
          </button>
        </div>
      )}
    </div>
  );
}
